import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getInternalContacts,
  getExternalContacts,
  createExternalCompany,
  createExternalContact,
  createStandaloneContact,
  updateExternalCompany,
  updateExternalContact,
  updateInternalContact,
  deleteExternalCompany,
  deleteExternalContact,
  uploadClientCompanyLogo,
  batchUpdateCompanies,
  batchUpdateContacts,
} from "../../services/directoryService";
import { getCustomFields } from "../../services/customFieldService";
import { useCurrentUser } from "../../context/UserContext";
import { useCurrency } from "../../hooks/useCurrency";
import { usePermissions } from "../../hooks/usePermission";
import FormModal from "../../components/forms/FormModal";
import DataTable from "../../components/forms/DataTable";
import CustomDropdown from "../../components/forms/CustomDropdown";
import ConfirmDialog from "../../components/ConfirmDialog";
import Toast from "../../components/Toast";
import SegmentedControl from "../../components/common/SegmentedControl";
import AddMenu from "../../components/common/AddMenu";
import BatchBar from "../../components/common/BatchBar";
import RowActionsMenu from "../../components/common/RowActionsMenu";
import Avatar from "../../components/common/Avatar";
import CustomFieldRows from "../../components/common/CustomFieldRows";
import CustomFieldModal from "../../components/settings/CustomFieldModal";
import chrome from "../../components/common/PageChrome.module.css";
import ui from "./DashboardUI.module.css";
import styles from "./directory/DirectoryUI.module.css";
import CompanyContactsTab from "./directory/CompanyContactsTab";
import CompanyWorkTab from "./directory/CompanyWorkTab";
import {
  DIRECTORY_TABS,
  DEFAULT_FILTERS,
  FILTERS,
  FINANCE_KEYS,
  NO_COMPANY,
  flattenContacts,
  primaryContactOf,
  sortDirectoryRows,
  exportColumns,
} from "./directory/directoryConfig";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import useTableColumns from "../../hooks/useTableColumns";
import useSelection from "../../hooks/useSelection";
import useBatchRunner from "../../hooks/useBatchRunner";
import { errorMessage } from "../../utils/errorMessage";
import { formatCompact } from "../../utils/costUtils";
import { avatarColorFromName } from "../../utils/avatarUtils";
import { toggleSort } from "../../utils/sortRows";
import ExportDialog from "../../components/common/ExportDialog";
import { customSortAccessor, customCellRenderers } from "../../utils/customFieldUtils";

const BLANK_COMPANY = {
  company_name: "",
  industry: "",
  website: "",
  company_phone: "",
  vat_number: "",
  billing_address: "",
  logo_url: "",
  company_notes: "",
  // Optional initial contact
  name: "",
  email: "",
  phone: "",
  job_title: "",
  department: "",
  is_primary: true,
  contact_notes: "",
};

const BLANK_CONTACT = {
  name: "",
  email: "",
  phone: "",
  job_title: "",
  department: "",
  client_company_id: "",
  is_primary: false,
  notes: "",
};

const COMPANY_TABS = [
  { key: "details", label: "Details" },
  { key: "contacts", label: "Contacts" },
  { key: "work", label: "Projects & leads" },
];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const ENTITY_FOR_TAB = { companies: "client_company", contacts: "client_contact" };

export default function Directory() {
  const currentUser = useCurrentUser();
  const { symbol: currencySymbol, format: formatMoney } = useCurrency();
  const {
    canViewInternalDirectory,
    canViewExternalDirectory,
    canCreateExternalContacts,
    canEditExternalContacts,
    canEditPeople,
    canViewProjectBudgets,
    canManageCompanySettings,
  } = usePermissions();

  const availableTabs = [
    ...(canViewExternalDirectory ? ["companies", "contacts"] : []),
    ...(canViewInternalDirectory ? ["team"] : []),
  ];
  const [savedTab, setTab] = useState(() => {
    try { return localStorage.getItem("directoryTab"); } catch { return null; }
  });
  const tab = availableTabs.includes(savedTab) ? savedTab : availableTabs[0] ?? "companies";
  useEffect(() => {
    try { if (tab) localStorage.setItem("directoryTab", tab); } catch { /* remembering the tab is optional */ }
  }, [tab]);

  const [team, setTeam] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [standalone, setStandalone] = useState([]);
  const [companyFields, setCompanyFields] = useState([]);
  const [contactFields, setContactFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);
  const showError = useCallback((message) => setToast({ message, type: "error" }), []);
  const runBatch = useBatchRunner(setToast);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [collapsedGroups, setCollapsedGroups] = useState({ companies: {}, contacts: {}, team: {} });
  const [addFieldEntity, setAddFieldEntity] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showIndustryForm, setShowIndustryForm] = useState(false);
  const [newIndustry, setNewIndustry] = useState("");

  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [companyForm, setCompanyForm] = useState(BLANK_COMPANY);
  const [contactForm, setContactForm] = useState(BLANK_CONTACT);
  const [logoFile, setLogoFile] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyTab, setCompanyTab] = useState("details");
  const [editingContact, setEditingContact] = useState(null);

  const columnStates = {
    companies: useTableColumns(DIRECTORY_TABS.companies.storageKey, DIRECTORY_TABS.companies.columns, companyFields),
    contacts: useTableColumns(DIRECTORY_TABS.contacts.storageKey, DIRECTORY_TABS.contacts.columns, contactFields),
    team: useTableColumns(DIRECTORY_TABS.team.storageKey, DIRECTORY_TABS.team.columns),
  };
  const selections = { companies: useSelection(), contacts: useSelection(), team: useSelection() };

  const loadDirectory = useCallback(async () => {
    const [internal, external] = await Promise.all([
      canViewInternalDirectory ? getInternalContacts() : null,
      canViewExternalDirectory ? getExternalContacts() : null,
    ]);
    if (internal) setTeam((internal.contacts || []).map((p) => ({ ...p, id: p.user_id })));
    if (external) {
      setCompanies((external.companies || []).map((c) => ({ ...c, id: c.client_company_id })));
      setStandalone(external.standalone_contacts || []);
    }
  }, [canViewInternalDirectory, canViewExternalDirectory]);

  const loadFields = useCallback(async (entity) => {
    const data = await getCustomFields(entity).catch(() => ({ fields: [] }));
    (entity === "client_company" ? setCompanyFields : setContactFields)(data.fields || []);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    Promise.all([
      loadDirectory(),
      canViewExternalDirectory ? loadFields("client_company") : null,
      canViewExternalDirectory ? loadFields("client_contact") : null,
    ])
      .catch((err) => showError(errorMessage(err, "Failed to load the directory.")))
      .finally(() => setLoading(false));
  }, [currentUser, canViewExternalDirectory, loadDirectory, loadFields, showError]);

  const contacts = useMemo(() => flattenContacts(companies, standalone), [companies, standalone]);

  const cfg = DIRECTORY_TABS[tab] || DIRECTORY_TABS.companies;
  const f = filters[tab] || DEFAULT_FILTERS.companies;
  const fields = { companies: companyFields, contacts: contactFields, team: [] }[tab] || [];
  const allRows = { companies, contacts, team }[tab] || [];
  const rows = sortDirectoryRows(FILTERS[tab](allRows, f), f.sort, tab, customSortAccessor(fields));
  const selection = selections[tab];
  const shownColumns = columnStates[tab].columns.filter((c) => canViewProjectBudgets || !FINANCE_KEYS.has(c.key));
  const group = cfg.groups[f.groupBy] || null;
  const canSelect = tab === "team" || canEditExternalContacts;
  const selectedRows = allRows.filter((r) => selection.selectedIds.has(r.id));

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, [tab]: { ...prev[tab], ...patch } }));
  const clearFilters = () => setFilters((prev) => ({ ...prev, [tab]: DEFAULT_FILTERS[tab] }));
  const showToast = (message, type = "success") => setToast({ message, type });

  const industries = [...new Set(companies.map((c) => c.industry).filter(Boolean))].sort();
  const departments = [...new Set(team.map((p) => p.department).filter(Boolean))].sort();
  const companyOptions = companies.map((c) => ({ value: String(c.client_company_id), label: c.company_name }));
  const filterOptions = {
    companies: [{ value: "", label: "All Industries" }, ...industries.map((i) => ({ value: i, label: i }))],
    contacts: [{ value: "", label: "All Companies" }, { value: "none", label: NO_COMPANY }, ...companyOptions],
    team: [{ value: "", label: "All Departments" }, ...departments.map((d) => ({ value: d, label: d }))],
  }[tab];
  const filterKey = { companies: "industry", contacts: "company", team: "department" }[tab];

  // ---- Export ----
  const [exporting, setExporting] = useState(null);

  // ---- Batch changes (every reversible one offers Undo) ----
  const setIndustry = (industry) => {
    const ids = selectedRows.map((r) => r.id);
    selection.clear();
    runBatch(
      () => batchUpdateCompanies({ ids, industry }),
      loadDirectory,
      (res) => `${plural(res.updated.length, "company", "companies")} ${industry ? `set to ${industry}` : "cleared"}.`,
      (res) => batchUpdateCompanies({ restore: res.previous }),
    );
  };

  const moveContacts = (target) => {
    const ids = selectedRows.map((r) => r.id);
    const name = target ? companies.find((c) => String(c.client_company_id) === target)?.company_name : NO_COMPANY;
    selection.clear();
    runBatch(
      () => batchUpdateContacts({ ids, client_company_id: target ? Number(target) : null }),
      loadDirectory,
      (res) => `${plural(res.updated.length, "contact", "contacts")} moved to ${name}.`,
      (res) => batchUpdateContacts({ restore: res.previous }),
    );
  };

  const deleteSelected = () => {
    const ids = selectedRows.map((r) => r.id);
    const isCompanies = tab === "companies";
    setConfirm({
      message: `Delete ${plural(ids.length, isCompanies ? "company" : "contact", isCompanies ? "companies" : "contacts")}? ${isCompanies ? `Contacts at ${ids.length === 1 ? "it" : "them"} are deleted too. ` : ""}This can't be undone.`,
      confirmText: "Delete",
      onConfirm: async () => {
        setConfirm(null);
        selection.clear();
        const results = await Promise.allSettled(ids.map(isCompanies ? deleteExternalCompany : deleteExternalContact));
        const failed = results.filter((r) => r.status === "rejected");
        await loadDirectory();
        const done = ids.length - failed.length;
        showToast(
          `${done} deleted${failed.length ? `; ${failed.length} kept. ${errorMessage(failed[0].reason, "")}` : "."}`,
          failed.length ? "error" : "success"
        );
      },
    });
  };

  // ---- Company and contact forms ----
  const afterSave = async (message, type = "success") => {
    await loadDirectory();
    showToast(message, type);
  };

  // Returns a message when the upload fails, so the save toast doesn't hide it.
  const uploadLogo = async (clientCompanyId) => {
    if (!logoFile) return null;
    try {
      await uploadClientCompanyLogo(clientCompanyId, logoFile);
      return null;
    } catch (err) {
      return errorMessage(err, "Failed to upload logo.");
    } finally {
      setLogoFile(null);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.company_name.trim()) return showError("Please enter a company name.");
    try {
      const result = await createExternalCompany(companyForm);
      const logoError = await uploadLogo(result.client_company_id);
      setCompanyForm(BLANK_COMPANY);
      setShowAddCompany(false);
      await (logoError ? afterSave(`Company added, but the logo failed: ${logoError}`, "error") : afterSave("Company added successfully."));
    } catch (err) {
      showError(errorMessage(err, "Failed to add company."));
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const { client_company_id, ...body } = contactForm;
      if (client_company_id) await createExternalContact(client_company_id, body);
      else await createStandaloneContact(body);
      setContactForm(BLANK_CONTACT);
      setShowAddContact(false);
      await afterSave("Contact added successfully.");
    } catch (err) {
      showError(errorMessage(err, "Failed to add contact."));
    }
  };

  const openCompany = (company) => {
    setEditingCompany(company);
    setCompanyTab("details");
  };

  // The PUT overwrites every column, so field edits send the saved row, not the form's unsaved edits.
  const saveCompanyField = async (field, value) => {
    try {
      const { custom_fields: _saved, contacts: _contacts, ...saved } = companies.find((c) => c.id === editingCompany.id);
      await updateExternalCompany(editingCompany.id, { ...saved, custom_fields: { [field.field_id]: value } });
      setEditingCompany((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [field.field_id]: value } }));
      await loadDirectory();
    } catch (err) {
      showError(errorMessage(err, "Failed to save."));
    }
  };

  const saveContactField = async (field, value) => {
    try {
      const { custom_fields: _saved, id: _id, company_name: _name, ...saved } = contacts.find((c) => c.id === editingContact.id);
      await updateExternalContact(editingContact.id, { ...saved, custom_fields: { [field.field_id]: value } });
      setEditingContact((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [field.field_id]: value } }));
      await loadDirectory();
    } catch (err) {
      showError(errorMessage(err, "Failed to save."));
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      const { custom_fields: _fields, contacts: _contacts, ...body } = editingCompany;
      await updateExternalCompany(editingCompany.id, body);
      const logoError = await uploadLogo(editingCompany.id);
      setEditingCompany(null);
      await (logoError ? afterSave(`Company updated, but the logo failed: ${logoError}`, "error") : afterSave("Company updated successfully."));
    } catch (err) {
      showError(errorMessage(err, "Failed to update company."));
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    try {
      const { custom_fields: _fields, id: _id, company_name: _name, ...body } = editingContact;
      if (editingContact.client_contact_id) {
        await updateExternalContact(editingContact.id, { ...body, client_company_id: body.client_company_id || null });
      } else {
        // Only these can be changed for an employee; job title and department live in People.
        await updateInternalContact(editingContact.user_id, {
          name: editingContact.name,
          email: editingContact.email,
          phone_number: editingContact.phone,
        });
      }
      setEditingContact(null);
      await afterSave("Contact updated successfully.");
    } catch (err) {
      showError(errorMessage(err, "Failed to update contact."));
    }
  };

  const confirmDelete = (message, remove) => setConfirm({
    message,
    confirmText: "Delete",
    onConfirm: async () => {
      setConfirm(null);
      try {
        await remove();
        setEditingCompany(null);
        setEditingContact(null);
        await afterSave("Deleted successfully.");
      } catch (err) {
        showError(errorMessage(err, "Failed to delete."));
      }
    },
  });

  // ---- Table cells ----
  const emailLink = (email) => (email
    ? <a className={chrome.emailLink} href={`mailto:${email}`} onClick={(e) => e.stopPropagation()}>{email}</a>
    : "—");
  const personCell = (name, avatarUrl, tag) => (
    <span className={styles.personCell}>
      <Avatar name={name} imageUrl={avatarUrl} size="sm" round />
      <span className={styles.personText}>
        <span className={styles.personName}>{name || "—"}</span>
        {tag && <span className={styles.primaryTag}>{tag}</span>}
      </span>
    </span>
  );

  const companyCells = {
    name: (c) => (
      <span className={chrome.nameCell}>
        <Avatar name={c.company_name} imageUrl={c.logo_url} size="sm" color={avatarColorFromName(c.company_name)} />
        <span>{c.company_name}</span>
      </span>
    ),
    industry: (c) => (c.industry ? <span className={styles.industryPill}>{c.industry}</span> : "—"),
    primary_contact: (c) => {
      const primary = primaryContactOf(c);
      if (!primary) return "—";
      const more = c.contacts.length - 1;
      return (
        <span className={styles.personCell}>
          <Avatar name={primary.name} size="sm" round />
          <span className={styles.personText}>
            <span className={styles.personName}>{primary.name || "—"}</span>
            {(more > 0 || primary.job_title) && (
              <span className={chrome.cellSub}>{more > 0 ? `+${more} more` : primary.job_title}</span>
            )}
          </span>
        </span>
      );
    },
    projects: (c) => (c.project_count || c.open_lead_count ? (
      <span className={styles.personText}>
        <span>{c.project_count || "—"}</span>
        {c.open_lead_count > 0 && <span className={chrome.cellSub}>{plural(c.open_lead_count, "open lead", "open leads")}</span>}
      </span>
    ) : "—"),
    value: (c) => (c.value ? (
      <span className={styles.personText}>
        <span className={styles.valueCell}>{formatCompact(currencySymbol, c.value)}</span>
        {c.lead_value > 0 && <span className={chrome.cellSub}>{formatCompact(currencySymbol, c.lead_value)} in leads</span>}
      </span>
    ) : "—"),
    phone: (c) => c.company_phone || "—",
    website: (c) => (c.website
      ? <a className={chrome.emailLink} href={c.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{c.website}</a>
      : "—"),
    billing_address: (c) => c.billing_address || "—",
    vat_number: (c) => c.vat_number || "—",
    contacts: (c) => c.contacts.length || "—",
    notes: (c) => c.company_notes || "—",
    ...customCellRenderers(companyFields, formatMoney),
  };

  const contactCells = {
    name: (c) => personCell(c.name, null, c.is_primary ? "Primary" : null),
    company: (c) => c.company_name || "—",
    job_title: (c) => c.job_title || "—",
    email: (c) => emailLink(c.email),
    phone: (c) => c.phone || "—",
    department: (c) => c.department || "—",
    notes: (c) => c.notes || "—",
    ...customCellRenderers(contactFields, formatMoney),
  };

  const teamCells = {
    name: (p) => personCell(p.name, p.avatar_url),
    job_title: (p) => p.job_title || "—",
    email: (p) => emailLink(p.email),
    phone: (p) => p.phone || "—",
    department: (p) => p.department || "—",
  };

  const cells = { companies: companyCells, contacts: contactCells, team: teamCells }[tab];
  const openRow = tab === "companies" ? openCompany : setEditingContact;

  // ---- Modals ----
  const set = (setter) => (key) => (e) => setter((prev) => ({ ...prev, [key]: e.target.value }));
  const text = (label, value, onChange, extra = {}) => ({ label, value, onChange, ...extra });

  const companyFormFields = (form, setForm, { editing = false, disabled = false } = {}) => {
    const update = set(setForm);
    return [
    text("Company Name *", form.company_name || "", update("company_name"), { disabled }),
    text("Industry", form.industry || "", update("industry"), { disabled }),
    text("Website", form.website || "", update("website"), { disabled }),
    text("Phone", form.company_phone || "", update("company_phone"), { type: "tel", disabled }),
    text("VAT Number", form.vat_number || "", update("vat_number"), { disabled }),
    ...(disabled ? [] : [{
      label: "Company Logo",
      type: "file",
      accept: "image/*",
      onChange: (e) => setLogoFile(e.target.files[0]),
      note: editing && form.logo_url ? "Upload a new logo to replace the current one" : "Upload a company logo (PNG, JPG, JPEG - max 2MB)",
    }]),
    text("Billing Address", form.billing_address || "", update("billing_address"), { type: "textarea", disabled }),
    text(editing ? "Notes" : "Company Notes", form.company_notes || "", update("company_notes"), { type: "textarea", disabled }),
    ];
  };

  const contactFormFields = (form, setForm, { external, disabled = false }) => {
    const update = set(setForm);
    return [
    text("Name", form.name || "", update("name"), { disabled }),
    text("Email", form.email || "", update("email"), { type: "email", disabled }),
    text("Phone", form.phone || "", update("phone"), { type: "tel", disabled }),
    text("Job Title", form.job_title || "", update("job_title"), { disabled: disabled || !external }),
    text("Department", form.department || "", update("department"), { disabled: disabled || !external }),
    ...(external ? [
      text("Company", String(form.client_company_id || ""), update("client_company_id"), {
        type: "select",
        disabled,
        options: [{ value: "", label: NO_COMPANY }, ...companyOptions],
      }),
      ...(form.client_company_id ? [{
        label: "Primary Contact",
        type: "checkbox",
        checked: Boolean(form.is_primary),
        disabled,
        onChange: (e) => setForm((prev) => ({ ...prev, is_primary: e.target.checked })),
      }] : []),
      text("Notes", form.notes || "", update("notes"), { type: "textarea", disabled }),
    ] : []),
    ];
  };

  const editingCompanyLive = editingCompany && (companies.find((c) => c.id === editingCompany.id) || editingCompany);
  const externalContactEditing = Boolean(editingContact?.client_contact_id);
  const contactReadOnly = editingContact && !(externalContactEditing ? canEditExternalContacts : canEditPeople);

  const customRows = (rowFields, entity, disabled, onSave) => (rowFields.length > 0 ? [{
    type: "custom",
    render: () => <CustomFieldRows fields={rowFields} entity={entity} disabled={disabled} formatMoney={formatMoney} onSave={onSave} />,
  }] : []);

  if (showLoading) {
    return (
      <div className={ui.pageContainer}>
        <div className={ui.toolbarSkeleton}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={`directory-toolbar-skeleton-${idx}`} className={`${ui.skeletonBlock} ${ui.toolbarSkeletonPill}`} />
          ))}
        </div>
        <div className={ui.tableSkeleton}>
          <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
          {Array.from({ length: 8 }).map((_, idx) => (
            <div key={`directory-row-skeleton-${idx}`} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={ui.fadeInContent}>
      {toast && (
        <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => setToast(null)} />
      )}

      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft} style={{ alignItems: "center" }}>
          <h1 className={ui.pageTitle}>Directory</h1>
          {availableTabs.length > 1 && (
            <SegmentedControl
              ariaLabel="Companies, contacts or team"
              options={availableTabs.map((key) => ({
                value: key,
                label: DIRECTORY_TABS[key].label,
                count: { companies, contacts, team }[key].length,
              }))}
              value={tab}
              onChange={setTab}
            />
          )}
        </div>
        <div className={ui.pageHeaderRight}>
          <RowActionsMenu actions={[{ label: "Export…", onClick: () => setExporting("filtered") }]} />
          {canCreateExternalContacts && (
            <AddMenu
              items={[
                { label: "Company", onClick: () => setShowAddCompany(true) },
                { label: "Contact", onClick: () => setShowAddContact(true) },
              ]}
            />
          )}
        </div>
      </div>

      <div className={ui.tableToolbar}>
        <input
          type="text"
          className={ui.toolbarSearch}
          placeholder={cfg.searchPlaceholder}
          aria-label={cfg.searchPlaceholder}
          value={f.search}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        <CustomDropdown
          toolbar
          options={filterOptions}
          value={f[filterKey]}
          onChange={(v) => setFilter({ [filterKey]: v })}
        />
        <CustomDropdown
          toolbar
          options={[
            { value: "", label: "No Grouping" },
            ...Object.entries(cfg.groups).map(([value, g]) => ({ value, label: `Group by ${g.label}` })),
          ]}
          value={f.groupBy}
          onChange={(v) => setFilter({ groupBy: v })}
        />
        <button type="button" className={ui.toolbarReset} onClick={clearFilters}>
          Clear
        </button>
      </div>

      <BatchBar count={selection.selectedIds.size} onClear={selection.clear}>
        {tab === "companies" && canEditExternalContacts && (
          <CustomDropdown
            toolbar
            value=""
            options={[
              { value: "", label: "Set industry…" },
              { value: "__new", label: "New industry…" },
              { value: "__none", label: "No industry" },
              ...industries.map((i) => ({ value: i, label: i })),
            ]}
            onChange={(v) => {
              if (v === "__new") setShowIndustryForm(true);
              else if (v) setIndustry(v === "__none" ? null : v);
            }}
          />
        )}
        {tab === "contacts" && canEditExternalContacts && (
          <CustomDropdown
            toolbar
            value=""
            options={[{ value: "", label: "Move to company…" }, { value: "__none", label: NO_COMPANY }, ...companyOptions]}
            onChange={(v) => v && moveContacts(v === "__none" ? null : v)}
          />
        )}
        <button type="button" onClick={() => setExporting("selected")}>Export</button>
        {tab !== "team" && canEditExternalContacts && (
          <button type="button" onClick={deleteSelected}>Delete</button>
        )}
      </BatchBar>

      {rows.length === 0 ? (
        <p className={styles.tabEmpty} style={{ padding: "24px 20px" }}>
          {allRows.length === 0 ? `No ${cfg.label.toLowerCase()} yet.` : `No ${cfg.label.toLowerCase()} match these filters.`}
        </p>
      ) : (
        <DataTable
          key={tab}
          columns={shownColumns}
          data={rows}
          storageKey={cfg.storageKey}
          groupedBy={group ? group.get : null}
          getGroupColor={() => "#85B7EB"}
          collapsedGroups={collapsedGroups[tab]}
          toggleGroupCollapse={(g) => setCollapsedGroups((prev) => ({ ...prev, [tab]: { ...prev[tab], [g]: !prev[tab][g] } }))}
          sortColumn={f.sort?.key}
          sortDirection={f.sort?.dir || "asc"}
          onSort={(key) => setFilter({ sort: toggleSort(f.sort, key) })}
          onRowClick={openRow}
          onToggleColumn={columnStates[tab].toggleColumn}
          onMoveColumn={columnStates[tab].moveColumn}
          onAddDetail={tab !== "team" && canManageCompanySettings ? () => setAddFieldEntity(ENTITY_FOR_TAB[tab]) : null}
          selectable={canSelect}
          selectedIds={selection.selectedIds}
          getRowId={(row) => row.id}
          onToggleRow={selection.toggleRow}
          onToggleGroup={selection.toggleGroup}
          onToggleAll={(checked) => selection.toggleAll(rows.map((r) => r.id), checked)}
          customCellRender={cells}
        />
      )}

      {exporting && (
        <ExportDialog
          title={`Export ${cfg.label.toLowerCase()}`}
          filename={cfg.exportName}
          scopes={[
            { value: "filtered", label: "Rows shown", rows },
            ...(selectedRows.length ? [{ value: "selected", label: "Ticked rows", rows: selectedRows }] : []),
            { value: "all", label: "Everything", rows: allRows },
          ]}
          defaultScope={exporting}
          columns={exportColumns(tab, shownColumns, fields)}
          groups={Object.entries(cfg.groups).map(([value, g]) => ({ value, label: g.label, get: g.get }))}
          defaultGroup={f.groupBy}
          onExported={(count, kind) => showToast(`${plural(count, "row", "rows")} exported${kind === "csv" ? " as CSV" : ""}.`)}
          onClose={() => setExporting(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          confirmText={confirm.confirmText}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {addFieldEntity && (
        <CustomFieldModal
          defaultEntity={addFieldEntity}
          onClose={() => setAddFieldEntity(null)}
          onSaved={() => loadFields(addFieldEntity)}
          onMessage={setToast}
        />
      )}

      {showIndustryForm && (
        <FormModal
          title="Set Industry"
          onCancel={() => { setShowIndustryForm(false); setNewIndustry(""); }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!newIndustry.trim()) return;
            setIndustry(newIndustry.trim());
            setShowIndustryForm(false);
            setNewIndustry("");
          }}
          fields={[text("Industry", newIndustry, (e) => setNewIndustry(e.target.value))]}
        />
      )}

      {showAddCompany && (
        <FormModal
          title="Add Company"
          onCancel={() => { setShowAddCompany(false); setCompanyForm(BLANK_COMPANY); setLogoFile(null); }}
          onSubmit={handleAddCompany}
          fields={[
            ...companyFormFields(companyForm, setCompanyForm),
            { type: "divider", label: "Initial Contact (Optional)" },
            text("Name", companyForm.name, set(setCompanyForm)("name")),
            text("Email", companyForm.email, set(setCompanyForm)("email"), { type: "email" }),
            text("Phone", companyForm.phone, set(setCompanyForm)("phone"), { type: "tel" }),
            text("Job Title", companyForm.job_title, set(setCompanyForm)("job_title")),
            text("Department", companyForm.department, set(setCompanyForm)("department")),
          ]}
        />
      )}

      {showAddContact && (
        <FormModal
          title="Add Contact"
          onCancel={() => { setShowAddContact(false); setContactForm(BLANK_CONTACT); }}
          onSubmit={handleAddContact}
          fields={contactFormFields(contactForm, setContactForm, { external: true })}
        />
      )}

      {editingCompany && (
        <FormModal
          title={canEditExternalContacts ? "Edit Company" : "Company"}
          onCancel={() => { setEditingCompany(null); setLogoFile(null); }}
          onSubmit={companyTab !== "details" ? null : canEditExternalContacts ? handleUpdateCompany : (e) => e.preventDefault()}
          overviewReadOnly={!canEditExternalContacts}
          tabs={COMPANY_TABS}
          activeTab={companyTab}
          onTabChange={setCompanyTab}
          footerActions={canEditExternalContacts ? (
            <button
              type="button"
              className={ui.buttonDanger}
              onClick={() => confirmDelete(
                "Are you sure you want to delete this company and all its contacts?",
                () => deleteExternalCompany(editingCompany.id),
              )}
            >
              Delete Company
            </button>
          ) : null}
          customTabContent={companyTab === "contacts" ? (
            <CompanyContactsTab
              company={editingCompanyLive}
              canEdit={canEditExternalContacts}
              onChanged={loadDirectory}
              onError={showError}
            />
          ) : companyTab === "work" ? (
            <CompanyWorkTab clientCompanyId={editingCompany.id} formatMoney={formatMoney} onError={showError} />
          ) : null}
          fields={[
            ...companyFormFields(editingCompany, setEditingCompany, { editing: true, disabled: !canEditExternalContacts }),
            ...customRows(companyFields, editingCompany, !canEditExternalContacts, saveCompanyField),
          ]}
        />
      )}

      {editingContact && (
        <FormModal
          title={contactReadOnly ? "Contact" : "Edit Contact"}
          onCancel={() => setEditingContact(null)}
          onSubmit={contactReadOnly ? (e) => e.preventDefault() : handleUpdateContact}
          overviewReadOnly={contactReadOnly}
          footerActions={externalContactEditing && canEditExternalContacts ? (
            <button
              type="button"
              className={ui.buttonDanger}
              onClick={() => confirmDelete(
                "Are you sure you want to delete this contact?",
                () => deleteExternalContact(editingContact.id),
              )}
            >
              Delete Contact
            </button>
          ) : null}
          fields={[
            ...contactFormFields(
              editingContact,
              setEditingContact,
              { external: externalContactEditing, disabled: contactReadOnly },
            ),
            ...(externalContactEditing ? customRows(contactFields, editingContact, contactReadOnly, saveContactField) : []),
          ]}
        />
      )}
    </div>
  );
}
